import {useEffect, useRef, useState} from 'react';
import {INPUT, TEXTAREA, TOGGLE} from '../../../../../components/cmp_form/cmp_form'
import {ListBox, ListBoxItem} from 'react-aria-components';
import ICON from '../../../../../components/cmp_icon/cmp_icon';

const OFFERING_OPTIONS = [
  { value: 'camps', text: 'Camps' },
  { value: 'dropIn', text: 'Drop-in' },
  { value: 'proDDay', text: 'Pro D Day' },
  { value: 'virtual', text: 'Virtual' },
  { value: 'freeTrial', text: 'Free trial' }
];

const ACCESSIBILITY_OPTIONS = [
  { value: 'adaptive', text: 'Adaptive' },
  { value: 'specialNeeds', text: 'Special needs' },
  { value: 'sensoryFriendly', text: 'Sensory friendly' },
  { value: 'wheelchairAccessible', text: 'Wheelchair accessible' }
];


export default function TAB_PROFILE({providerData, updateProvider, updateHost, onOpen}) {

  const provider = providerData?.extractions?.provider || {};
  const [selectedNav, setSelectedNav] = useState('basic');
  const contentRef = useRef(null);
  const programmaticScrollRef = useRef(false);

  const createSlug = (value) => value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const handleChange = (field, value) => {
    if (field === 'domainHost') {
      updateHost(value);
    } else if (field === 'name') {
      updateProvider({
        name: value,
        slug: createSlug(value)
      });
    } else {
      updateProvider({ [field]: value });
    }
  };

  const openExternalUrl = (value) => {
    const host = value?.trim();
    if (!host) return;

    const url = /^https?:\/\//i.test(host) ? host : `https://${host}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleOfferingChange = (offering, value) => {
    updateProvider({
      offerings: {
        ...provider.offerings,
        [offering]: value
      }
    });
  };

  const handleAccessibilityChange = (accessibility, value) => {
    updateProvider({
      accessibility: {
        ...provider.accessibility,
        [accessibility]: value
      }
    });
  };

  const scrollToSection = (id) => {
    const content = contentRef.current;
    const element = content?.querySelector(`#${id}`);
    if (element) {
      element.scrollIntoView({
        behavior: 'auto',
        block: 'start',
        inline: 'nearest'
      });
    }
  };

  const updateSelectedSection = () => {
    const content = contentRef.current;
    if (!content || programmaticScrollRef.current) return;

    const sections = Array.from(content.querySelectorAll(':scope > section'));
    const contentTop = content.getBoundingClientRect().top;
    const currentSection = sections.reduce((current, section) => {
      const sectionTop = section.getBoundingClientRect().top - contentTop;
      return sectionTop <= 0 ? section : current;
    }, sections[0]);

    if (currentSection?.id) {
      setSelectedNav(currentSection.id);
    }
  };

  useEffect(() => {
    updateSelectedSection();
  }, []);

  return (
    <div className='tab-profile'>

      <nav className='tab-profile__nav'>
        <ListBox
          aria-label="Profile sections"
          selectionMode="single"
          selectedKeys={[selectedNav]}
          onSelectionChange={(keys) => {
            const keyArray = Array.from(keys);
            const selectedKey = keyArray[0];
            if (selectedKey) {
              programmaticScrollRef.current = true;
              scrollToSection(selectedKey);
              setSelectedNav(selectedKey);
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  programmaticScrollRef.current = false;
                });
              });
            }
          }}
        >
          <ListBoxItem id="basic" key="basic">Basic information</ListBoxItem>
          <ListBoxItem id="descriptions" key="descriptions">Descriptions</ListBoxItem>
          <ListBoxItem id="offerings" key="offerings">Offerings</ListBoxItem>
          <ListBoxItem id="accessibility" key="accessibility">Accessibility</ListBoxItem>
          <ListBoxItem id="social" key="social">Social media</ListBoxItem>
        </ListBox>
      </nav>

      <div
        className='tab-profile__content'
        onScroll={updateSelectedSection}
        ref={contentRef}
      >

        <section id='basic'>
          <div className='text--md medium'>Basic information</div>
          <div className='subheading'>Core details used to identify and present the provider.</div>
          <INPUT
            description="The provider name as it should appear in Lively Village"
            id='name'
            label="Provider name"
            name='name'
            onChange={(e) => handleChange('name', e.target.value)}
            value={provider.name || ''}
          />

          <INPUT
            description="Unique slug for Lively Village navigation"
            id='slug'
            label="Slug"
            name='slug'
            disabled={true}
            onChange={(e) => handleChange('slug', e.target.value)}
            value={provider.slug || ''}
          />

          <INPUT
            description="The URL of the provider's website"
            id='domain'
            label="Provider website"
            name='domain'
            onChange={(e) => handleChange('domainHost', e.target.value)}
            action={<ICON name='link' className='icon search-icon' alt='link icon' />}
            actionLabel='Open provider website'
            onAction={() => openExternalUrl(providerData?.host)}
            value={providerData?.host || ''}
          />

          <INPUT
            description="Provider primary email"
            id='primaryEmail'
            label="Provider primary email"
            name='primaryEmail'
            onChange={(e) => handleChange('primaryEmail', e.target.value)}
            value={provider?.primaryEmail || ''}
          />

        </section>
        <section id='descriptions'>
          <div className='text--md medium'>Descriptions</div>
          <div className='subheading'>Public-facing copy used across provider and search pages.</div>
          <TEXTAREA
            description="SEO optimized description that appears on the provider page"
            id='description'
            label="Description"
            maxLength="1000"
            name='description'
            onChange={(e) => handleChange('description', e.target.value)}
            value={provider.description || ''}
          />

          <TEXTAREA
            description="The description that appears in search result pages"
            id='metaDescription'
            label="Meta Description"
            maxLength="200"
            name='metaDescription'
            onChange={(e) => handleChange('metaDescription', e.target.value)}
            value={provider.seoDescription || ''}
          />

        </section>

        <section id='offerings'>
          <div className='text--md medium'>Offerings</div>
          <div className='subheading'>Services and program formats available from the provider.</div>
          <div className='tab-profile__toggle-list'>
            {OFFERING_OPTIONS.map(({value, text}) => (
              <TOGGLE
                key={value}
                label={text}
                checked={provider.offerings?.[value] === true}
                onChange={(checked) => handleOfferingChange(value, checked)}
              />
            ))}
          </div>
        </section>
        <section id='accessibility'>
          <div className='text--md medium'>Accessibility</div>
          <div className='subheading'>Accessibility features and inclusive experiences offered by the provider.</div>
          <div className='tab-profile__toggle-list'>
            {ACCESSIBILITY_OPTIONS.map(({value, text}) => (
              <TOGGLE
                key={value}
                label={text}
                checked={provider.accessibility?.[value] === true}
                onChange={(checked) => handleAccessibilityChange(value, checked)}
              />
            ))}
          </div>
        </section>
        <section id='social'>
          <div className='text--md medium'>Social media</div>
          <div className='subheading'>Social channels visitors can use to connect with the provider.</div>

          <INPUT
            description="Link to provider Facebook account"
            id='facebook'
            label="Facebook"
            name='facebook'
            onChange={(e) => updateProvider({ social: { ...provider.social, facebook: e.target.value } })}
            action={<ICON name='link' className='icon search-icon' alt='link icon' />}
            actionLabel='Open provider Facebook account'
            onAction={() => openExternalUrl(provider.social?.facebook)}
            value={provider.social?.facebook || ''}
          />
          <INPUT
            description="Link to provider Instagram account"
            id='instagram'
            label="Instagram"
            name='instagram'
            onChange={(e) => updateProvider({ social: { ...provider.social, instagram: e.target.value } })}
            action={<ICON name='link' className='icon search-icon' alt='link icon' />}
            actionLabel='Open provider Instagram account'
            onAction={() => openExternalUrl(provider.social?.instagram)}
            value={provider.social?.instagram || ''}
          />
          <INPUT
            description="Link to provider TikTok account"
            id='tiktok'
            label="TikTok"
            name='tiktok'
            onChange={(e) => updateProvider({ social: { ...provider.social, tiktok: e.target.value } })}
            action={<ICON name='link' className='icon search-icon' alt='link icon' />}
            actionLabel='Open provider TikTok account'
            onAction={() => openExternalUrl(provider.social?.tiktok)}
            value={provider.social?.tiktok || ''}
          />
          <INPUT
            description="Link to provider YouTube account"
            id='youtube'
            label="YouTube"
            name='youtube'
            onChange={(e) => updateProvider({ social: { ...provider.social, youtube: e.target.value } })}
            action={<ICON name='link' className='icon search-icon' alt='link icon' />}
            actionLabel='Open provider YouTube account'
            onAction={() => openExternalUrl(provider.social?.youtube)}
            value={provider.social?.youtube || ''}
          />
          <INPUT
            description="Link to provider Twitter/X account"
            id='twitter'
            label="Twitter/X"
            name='twitter'
            onChange={(e) => updateProvider({ social: { ...provider.social, twitter: e.target.value } })}
            action={<ICON name='link' className='icon search-icon' alt='link icon' />}
            actionLabel='Open provider Twitter/X account'
            onAction={() => openExternalUrl(provider.social?.twitter)}
            value={provider.social?.twitter || ''}
          />
        </section>
      </div>
    </div>
  );
}